import { useMemo } from 'react';

import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import IconButton from '@zenbusiness/zui/IconButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';
import PropTypes from 'prop-types';

const WhatsNewBanner = ({
  title,
  pill,
  description,
  imageUrl,
  buttonProps,
  handleClose,
  variant = 'default'
}) => {
  const {
    sizes: { isMobile, isTablet }
  } = useBreakpoints();
  const theme = useTheme();
  const hasButton = !!buttonProps?.label;

  const variantProps = useMemo(
    () => ({
      default: {
        fill: 'secondary',
        title: 'primary',
        description: 'secondary',
        button: 'primary',
        backgroundColor: theme.palette.background.green.light,
        xIconColor: theme.palette.fixed.black
      },
      promo: {
        fill: 'white',
        title: 'white',
        description: 'white',
        button: 'secondary',
        backgroundColor: theme.palette.fixed.black,
        xIconColor: theme.palette.background.grey.light
      }
    }),
    [
      theme.palette.background.green.light,
      theme.palette.background.grey.light,
      theme.palette.fixed.black
    ]
  );

  return (
    <Stack
      gap="16px"
      height="100%"
      sx={{
        backgroundColor: variantProps[variant].backgroundColor,
        borderRadius: '12px',
        paddingX: { xs: '16px', md: '24x' },
        paddingY: { xs: '16px', md: '16px' }
      }}
    >
      <Stack
        alignItems="center"
        flexDirection="row"
        gap={{ xs: '12px', md: '16px' }}
        height="100%"
      >
        {imageUrl && (
          <SharedAsset
            alt=""
            src={imageUrl}
            width={isMobile ? 48 : 64}
          />
        )}
        <Stack gap="4px">
          <Text
            color={variantProps[variant].fill}
            size="xs"
            weight="medium"
          >
            {pill}
          </Text>
          <Text
            color={variantProps[variant].title}
            size="md"
            weight="medium"
          >
            {title}
          </Text>
          <Text
            color={variantProps[variant].description}
            size="sm"
          >
            {description}
          </Text>
        </Stack>
        <Stack
          alignSelf={{ xs: 'flex-start', lg: 'center' }}
          flexDirection="row"
          marginLeft="auto"
        >
          {hasButton && !isTablet && !isMobile && (
            <Box
              flex="1"
              minWidth="120px"
            >
              <Button
                color={variantProps[variant].button}
                onClick={buttonProps.onClick}
                size="medium"
                variant="contained"
              >
                {buttonProps.label}
              </Button>
            </Box>
          )}
          <IconButton
            color="primary"
            label="close"
            onClick={handleClose}
            size="small"
            sx={{ flex: 0 }}
          >
            <Box minWidth={24}>
              <SharedAsset
                alt="Close icon"
                color={variantProps[variant].xIconColor}
                src="/icon/micro/close-line.svg"
              />
            </Box>
          </IconButton>
        </Stack>
      </Stack>
      {hasButton && (isTablet || isMobile) && (
        <Box width="99%">
          <Button
            color={variantProps[variant].button}
            fullWidth
            onClick={buttonProps.onClick}
            size="medium"
            variant="contained"
          >
            {buttonProps.label}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

WhatsNewBanner.propTypes = {
  /**
   * The title of the banner.
   */
  title: PropTypes.string.isRequired,
  /**
   * The description of the banner.
   */
  description: PropTypes.string.isRequired,
  /**
   * The pill of the banner.
   */
  pill: PropTypes.string,
  /** Slots for the banner. */
  slotProps: PropTypes.shape({}),
  imageUrl: PropTypes.string,
  handleClose: PropTypes.func,
  buttonProps: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func
  }),
  variant: PropTypes.string
};

export default WhatsNewBanner;
